import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import MoveDownRoundedIcon from '@mui/icons-material/MoveDownRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import { SectionCard, EmptyState, StatusChip, type StatusTone } from '../ui';
import { paymentService, type SweepStatus } from '../../services/paymentService';

interface Props {
  onToast: (msg: string, sev: 'success' | 'error') => void;
}

const fmt = (n: number | null | undefined, d = 2): string =>
  n == null ? '—' : Number(n).toFixed(d);

const fmtDate = (ms: number | null): string => {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString('vi-VN');
  } catch {
    return String(ms);
  }
};

/** Ô số liệu — giống nhịp của PricingEditor để hai tab đọc như một. */
const Stat: React.FC<{ k: string; v: string; tone?: StatusTone; action?: React.ReactNode }> = ({ k, v, tone, action }) => (
  <Box
    sx={{
      bgcolor: 'action.hover',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      px: 2,
      py: 1.5,
      minWidth: 150,
      flex: 1,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
      {k}
    </Typography>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: '1.2rem',
        mt: 0.3,
        color: tone === 'warning' ? 'warning.main' : tone === 'error' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
      }}
    >
      {v}
    </Typography>
    {action}
  </Box>
);

export const SweepPanel: React.FC<Props> = ({ onToast }) => {
  const [chains, setChains] = useState<SweepStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ chain: SweepStatus; hdIndex?: number } | null>(null);
  const [gasQr, setGasQr] = useState<{ address: string; native: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await paymentService.getSweepStatus();
      setChains(r.chains ?? []);
    } catch (e) {
      onToast('Không đọc được tình trạng gom tiền: ' + (e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const doSweep = async () => {
    if (!confirm) return;
    const { chain, hdIndex } = confirm;
    setBusy(chain.providerId);
    setConfirm(null);
    try {
      const r = await paymentService.sweepChain(chain.providerId, hdIndex);
      if (r.error) throw new Error(r.error);
      const ok = r.results.filter((x) => x.ok).length;
      const failed = r.results.filter((x) => !x.ok);
      onToast(
        `Đã gom ${ok}/${r.results.length} địa chỉ · ${fmt(r.swept)} USDT` +
          (failed.length ? ` · ${failed.length} địa chỉ hỏng: ${failed[0].message}` : ''),
        failed.length ? 'error' : 'success',
      );
      await load();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (!chains.length) {
    return (
      <EmptyState
        title="Chưa có mạng on-chain nào"
        description="Bật cổng USDT (BEP20 hoặc Polygon) ở tab Cổng thanh toán trước."
      />
    );
  }

  return (
    <Stack spacing={3}>
      {chains.map((c) => {
        const gasLow = c.gasFunder?.refillsLeft != null && c.gasFunder.refillsLeft < 3;
        const blocked = !c.walletMatches || !c.sweepTo || !!c.error;

        return (
          <SectionCard
            key={c.providerId}
            title={c.networkLabel}
            description={c.sweepTo ? `Gom về ${c.sweepTo}` : 'Chưa đặt ví đích'}
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={() => void load()}>
                  Tải lại
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<MoveDownRoundedIcon />}
                  disabled={blocked || busy === c.providerId || !c.totals.willSweep}
                  onClick={() => setConfirm({ chain: c })}
                >
                  {busy === c.providerId ? 'Đang gom…' : 'Gom ngay'}
                </Button>
              </Stack>
            }
          >
            {c.error && <Alert severity="error">Không đọc được mạng: {c.error}</Alert>}

            {!c.error && !c.walletMatches && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Không gom được</AlertTitle>
                {c.walletError}
              </Alert>
            )}

            {!c.error && c.walletMatches && !c.sweepTo && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Chưa đặt <b>ví đích</b>. Điền trường <code>sweepTo</code> ở tab Cổng thanh toán —
                nên là ví lạnh, đừng dùng ví có khoá nằm trong hệ thống.
              </Alert>
            )}

            {!c.error && (
              <>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Stat
                    k="Chưa gom"
                    v={`${fmt(c.totals.sweepable)} USDT`}
                    tone={c.overAlert ? 'warning' : c.totals.sweepable > 0 ? 'success' : undefined}
                  />
                  <Stat k="Số địa chỉ" v={String(c.totals.willSweep)} />
                  <Stat
                    k="Ví trả gas"
                    v={`${fmt(c.gasFunder?.balance, 5)} ${c.native}`}
                    tone={gasLow ? 'warning' : undefined}
                    action={
                      c.gasFunder ? (
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={() => setGasQr({ address: c.gasFunder!.address, native: c.native })}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}
                        >
                          <LocalGasStationRoundedIcon sx={{ fontSize: 14 }} /> Nạp gas
                        </Link>
                      ) : undefined
                    }
                  />
                  <Stat
                    k="Bơm được thêm"
                    v={c.gasFunder?.refillsLeft == null ? '—' : `${c.gasFunder.refillsLeft} lần`}
                    tone={gasLow ? 'warning' : undefined}
                  />
                  <Stat k="Gas mỗi lần gom" v={`${fmt(c.gasPerSweep, 6)} ${c.native}`} />
                </Stack>

                {c.overAlert && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Tiền chưa gom ({fmt(c.totals.sweepable)} USDT) đã vượt ngưỡng cảnh báo{' '}
                    {c.alertUsdt} USDT. Gom ngay, hoặc kiểm tra vì sao lượt gom tự động không chạy.
                  </Alert>
                )}

                {gasLow && c.gasFunder && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Ví trả gas sắp cạn
                    {c.gasFunder.refillsLeft != null ? ` (còn ~${c.gasFunder.refillsLeft} lần bơm)` : ''}. Bấm{' '}
                    <b>Nạp gas</b> để nạp thêm {c.native}, nếu không lượt gom sẽ dừng giữa chừng.
                  </Alert>
                )}

                {c.rows.length === 0 ? (
                  <EmptyState title="Không có địa chỉ nào đang giữ tiền" dense />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="right">#</TableCell>
                          <TableCell>Tài khoản</TableCell>
                          <TableCell>Địa chỉ nhận</TableCell>
                          <TableCell align="right">USDT</TableCell>
                          <TableCell>Tình trạng</TableCell>
                          <TableCell>Gom lần cuối</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {c.rows.map((r) => (
                          <TableRow key={r.hdIndex} hover>
                            <TableCell align="right">{r.hdIndex}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{r.email}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '.78rem', wordBreak: 'break-all' }}>
                              {r.address}
                            </TableCell>
                            <TableCell align="right">{fmt(r.usdt)}</TableCell>
                            <TableCell>
                              {!r.worthIt ? (
                                <StatusChip label="quá ít, để lần sau" tone="neutral" title="Phí gas còn hơn số tiền" />
                              ) : r.needsGas ? (
                                <StatusChip label="cần bơm gas" tone="warning" />
                              ) : (
                                <StatusChip label="gom được" tone="success" />
                              )}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '.8rem' }}>{fmtDate(r.sweptAt)}</TableCell>
                            <TableCell align="right">
                              {r.willSweep && !blocked && (
                                <Button
                                  size="small"
                                  disabled={busy === c.providerId}
                                  onClick={() => setConfirm({ chain: c, hdIndex: r.hdIndex })}
                                >
                                  Gom
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {c.totals.skippedTooSmall > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {c.totals.skippedTooSmall} địa chỉ có quá ít USDT — phí gas còn hơn số tiền, nên
                    để lại gom lần sau.
                  </Typography>
                )}
              </>
            )}
          </SectionCard>
        );
      })}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Xác nhận gom tiền</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.hdIndex === undefined
              ? `Gom ${confirm?.chain.totals.willSweep} địa chỉ (${fmt(confirm?.chain.totals.sweepable)} USDT)`
              : `Gom địa chỉ số ${confirm?.hdIndex}`}{' '}
            trên {confirm?.chain.networkLabel}, về ví:
          </DialogContentText>
          <Box component="code" sx={{ display: 'block', my: 1.5, wordBreak: 'break-all', fontSize: '.85rem' }}>
            {confirm?.chain.sweepTo}
          </Box>
          <Alert severity="warning">
            Giao dịch trên blockchain <b>không thể hoàn lại</b>. Kiểm tra ví đích ở trên trước khi
            bấm.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Huỷ</Button>
          <Button variant="contained" onClick={() => void doSweep()}>
            Gom tiền
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!gasQr} onClose={() => setGasQr(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nạp gas{gasQr ? ` (${gasQr.native})` : ''}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {gasQr && (
            <>
              <Box
                component="img"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(gasQr.address)}`}
                alt={`QR địa chỉ ví trả gas ${gasQr.native}`}
                sx={{ width: 220, height: 220, mb: 2, bgcolor: '#fff', borderRadius: 1 }}
              />
              <Box component="code" sx={{ display: 'block', wordBreak: 'break-all', fontSize: 13, mb: 1.5 }}>
                {gasQr.address}
              </Box>
              <Button
                variant="outlined"
                startIcon={<ContentCopyRoundedIcon fontSize="small" />}
                onClick={() => {
                  void navigator.clipboard?.writeText(gasQr.address);
                  onToast('Đã copy địa chỉ ví trả gas', 'success');
                }}
              >
                Copy địa chỉ
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGasQr(null)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="body2" color="text.secondary">
        Hệ thống tự gom mỗi 5 giờ. Nút ở đây dành cho lúc cần gom gấp hoặc sau khi vừa nạp thêm gas.
        Mỗi lượt xử lý tối đa vài địa chỉ để một lần bấm không chạy quá lâu — bấm lại để gom tiếp.{' '}
        <Link href="#" onClick={(e) => { e.preventDefault(); void load(); }}>
          Tải lại
        </Link>
      </Typography>
    </Stack>
  );
};
